import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { schoolPride } from "../lib/confetti";

type Person = "karishma" | "ben";

type Question = {
  id: string;
  prompt: string;
  /** Who the true answer is. */
  answer: Person;
  /** Optional real numbers, revealed after answering. Keyed by person. */
  stat?: { karishma: string; ben: string };
  /** Optional extra flavor after the numbers. By default it's appended inline
   *  ("… vs. 23, including DC."); set `noteBreak` to drop it onto its own line. */
  note?: string;
  noteBreak?: boolean;
  /** Optional per-person circle photo (public path). Falls back to an initial
   *  disc for any side without one. */
  photos?: Partial<Record<Person, string>>;
  /** Set while the true answer isn't confirmed yet — keeps it out of play. */
  pending?: boolean;
};

const NAMES: Record<Person, string> = { karishma: "Karishma", ben: "Ben" };

// Reveal reactions, picked at random per answer.
const SUCCESS = ["Nailed it", "Nice one", "Boom", "Good job"];
const FAILURE = ["Oops…", "Nope", "Sorry…"];
const pickFrom = (pool: string[]) =>
  pool[Math.floor(Math.random() * pool.length)]!;

// The questions. `answer` is the real answer; `stat` (optional) shows the actual
// numbers on reveal. Entries marked `pending` need a confirmed answer before
// they go live.
const QUESTIONS: Question[] = [
  {
    id: "older",
    prompt: "Who’s older?",
    answer: "ben",
    note: "by 2 months and 15 days!",
    photos: {
      karishma: "/shoegame/karishma-age.webp",
      ben: "/shoegame/ben-age.webp",
    },
  },
  {
    id: "unread-emails",
    prompt: "Who has the most unread emails (at time of writing)?",
    answer: "ben",
    stat: { ben: "157,823", karishma: "29,034" },
    note: "Yikes.",
    photos: {
      ben: "/shoegame/ben-emails.webp",
      karishma: "/shoegame/karishma-emails.webp",
    },
  },
  {
    id: "uber-rating",
    prompt: "Who has the higher Uber rating?",
    answer: "karishma",
    stat: { ben: "4.76", karishma: "4.91" },
    photos: {
      karishma: "/shoegame/karishma-uber.webp",
      ben: "/shoegame/ben-uber.webp",
    },
  },
  {
    id: "night-owl",
    prompt: "Who’s the night owl?",
    answer: "karishma",
    photos: {
      karishma: "/shoegame/karishma-nightowl.webp",
      ben: "/shoegame/ben-nightowl.webp",
    },
  },
  {
    id: "broken-bones",
    prompt: "Who has broken more bones?",
    answer: "karishma",
    stat: { karishma: "1", ben: "0 😇" },
    note: "(she got her finger kicked during Taekwondo)",
    noteBreak: true,
    photos: {
      ben: "/shoegame/ben-bones.webp",
      karishma: "/shoegame/karishma-bones.webp",
    },
  },
  {
    id: "eyesight",
    prompt: "Who has better eyesight?",
    answer: "ben",
    stat: { ben: "20/16", karishma: "20/20" },
    photos: {
      ben: "/shoegame/ben-eyesight.webp",
      karishma: "/shoegame/karishma-eyesight.webp",
    },
    note: "Sometimes, perfect isn’t perfect enough.",
    noteBreak: true,
  },
  {
    id: "us-states",
    prompt: "Who’s visited more US states?",
    answer: "karishma",
    stat: { karishma: "28", ben: "23" },
    note: "including (most importantly) DC.",
    photos: {
      karishma: "/shoegame/karishma-states.webp",
      ben: "/shoegame/ben-states.webp",
    },
  },
  {
    id: "morning-alarms",
    prompt: "Who sets more morning alarms?",
    answer: "ben",
    stat: { ben: "8", karishma: "4" },
    photos: {
      ben: "/shoegame/ben-alarm.webp",
      karishma: "/shoegame/karish-alarm.webp",
    },
  },
];

// Only confirmed questions are played.
const LIVE = QUESTIONS.filter((q) => !q.pending);

const SHARE_TITLE = "K or B?";

export function ShoeGame({ guestId }: { guestId: string }) {
  // step runs 0..LIVE.length; the final value is the results screen.
  const [step, setStep] = useState(0);
  const [guesses, setGuesses] = useState<Record<string, Person>>({});
  // The random reaction shown per question, fixed at answer time so it doesn't
  // reshuffle on re-render.
  const [reactions, setReactions] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);

  const done = step >= LIVE.length;
  const q = LIVE[step];
  const guess = q ? guesses[q.id] : undefined;
  const revealed = Boolean(guess);
  const score = LIVE.filter((x) => guesses[x.id] === x.answer).length;

  const pick = (p: Person) => {
    if (!q || guesses[q.id]) return;
    setGuesses((g) => ({ ...g, [q.id]: p }));
    setReactions((r) => ({
      ...r,
      [q.id]: pickFrom(p === q.answer ? SUCCESS : FAILURE),
    }));
  };

  // Advance; on the last question, log the finished score + per-question
  // breakdown first (fire-and-forget).
  const advance = () => {
    if (step === LIVE.length - 1) {
      const answers = LIVE.map((x) => ({
        questionId: x.id,
        prompt: x.prompt,
        guess: guesses[x.id] ?? "",
        correct: guesses[x.id] === x.answer,
      }));
      void api
        .post("/games/kb-score", { guestId, score, total: LIVE.length, answers })
        .catch(() => {});
    }
    setStep((s) => s + 1);
  };

  // Celebrate a clean sweep.
  useEffect(() => {
    if (done && score === LIVE.length) return schoolPride();
  }, [done, score]);

  const share = useMemo(() => {
    const squares = LIVE.map((x) =>
      guesses[x.id] === x.answer ? "🟩" : "⬛",
    ).join("");
    return `${SHARE_TITLE}\n${squares}  ${score}/${LIVE.length}`;
  }, [guesses, score]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(share);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — the text is shown selectable below regardless.
    }
  };

  // ── Results ────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="mx-auto max-w-xl text-center">
        <span
          aria-hidden="true"
          className="font-serif text-3xl text-dusty-rose"
        >
          ❃
        </span>
        <h2 className="mt-4 font-serif text-4xl text-pine sm:text-5xl">
          You got&hellip; {score}/{LIVE.length}!
        </h2>
        <pre className="mx-auto mt-6 inline-block whitespace-pre-wrap bg-pine/[0.04] px-6 py-4 text-left font-sans text-base leading-relaxed text-pine">
          {share}
        </pre>
        <div className="mt-6">
          <button
            type="button"
            onClick={copy}
            className="bg-midnight-teal px-8 py-3 font-sans text-sm font-medium uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pine"
          >
            {copied ? "Copied!" : "Copy result"}
          </button>
        </div>
      </div>
    );
  }

  // ── One question at a time ──────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-xl text-center">
      <h2 className="font-serif text-4xl text-pine sm:text-5xl">K or B?</h2>

      {/* Progress — a diamond per question: green once answered right, pink if
          wrong, teal for the one in play, faint for what's still ahead. */}
      <div aria-hidden="true" className="mt-6 flex justify-center gap-2.5">
        {LIVE.map((x, k) => {
          const answer = guesses[x.id];
          const color = answer
            ? answer === x.answer
              ? "bg-sage-moss"
              : "bg-dusty-rose"
            : k === step
              ? "bg-midnight-teal"
              : "bg-pine/20";
          return (
            <span
              key={x.id}
              className={`inline-block h-1.5 w-1.5 rotate-45 ${color}`}
            />
          );
        })}
      </div>

      {/* Keyed by question so each new card animates in. */}
      <div key={q!.id} className="rise-in mt-8">
        <p className="mx-auto max-w-md font-serif text-3xl leading-snug text-pine sm:text-4xl">
          {q!.prompt}
        </p>

        <div className="mt-8 flex items-center justify-center gap-4 sm:gap-10">
          <Choice
            person="karishma"
            photo={q!.photos?.karishma}
            answer={q!.answer}
            guess={guess}
            revealed={revealed}
            onPick={pick}
          />
          <span className="font-serif text-xl italic text-pine/40 sm:text-3xl">
            or
          </span>
          <Choice
            person="ben"
            photo={q!.photos?.ben}
            answer={q!.answer}
            guess={guess}
            revealed={revealed}
            onPick={pick}
          />
        </div>

        {revealed && (
          <div className="rise-in mt-8">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-pine/50">
              {reactions[q!.id]}
            </p>
            {q!.stat ? (
              <>
                <p className="mt-3 font-serif text-2xl text-pine">
                  {NAMES[q!.answer]}
                </p>
                <p className="mt-1 font-serif text-6xl text-midnight-teal lining-nums tabular-nums">
                  {q!.stat[q!.answer]}
                </p>
                <p className="mt-1 font-sans text-base text-pine/60">
                  to {NAMES[q!.answer === "ben" ? "karishma" : "ben"]}’s{" "}
                  {q!.stat[q!.answer === "ben" ? "karishma" : "ben"]}
                  {q!.note && !q!.noteBreak
                    ? // Lowercase note = a continuation (", including…"); an
                      // uppercase one is its own sentence (". Yikes.").
                      `${/^[a-z]/.test(q!.note) ? "," : "."} ${q!.note}`
                    : "."}
                </p>
                {q!.note && q!.noteBreak && (
                  <p className="mx-auto mt-2 max-w-xs font-sans text-base text-pine/55">
                    {q!.note}
                  </p>
                )}
              </>
            ) : (
              <p className="mt-3 font-serif text-4xl text-midnight-teal">
                It’s {NAMES[q!.answer]}
                {q!.note ? ` — ${q!.note}` : "!"}
              </p>
            )}

            <button
              type="button"
              onClick={advance}
              className="mt-8 bg-midnight-teal px-10 py-3 font-sans text-sm font-medium uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pine"
            >
              {step === LIVE.length - 1 ? "See results" : "Next"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** A clickable, circle-cut choice. Uses the question's per-person photo when one
 *  exists, otherwise a tinted disc with the initial. On reveal the true answer
 *  gets a ring, the other dims, and a ✓/✗ badge marks the guess. */
function Choice({
  person,
  photo,
  answer,
  guess,
  revealed,
  onPick,
}: {
  person: Person;
  photo?: string;
  answer: Person;
  guess: Person | undefined;
  revealed: boolean;
  onPick: (p: Person) => void;
}) {
  const isAnswer = person === answer;
  const isGuess = person === guess;
  const disc = revealed
    ? isAnswer
      ? "ring-2 ring-midnight-teal ring-offset-4 ring-offset-cream"
      : "opacity-40"
    : "ring-2 ring-transparent ring-offset-4 ring-offset-cream hover:ring-midnight-teal/40";
  return (
    <button
      type="button"
      disabled={revealed}
      onClick={() => onPick(person)}
      aria-label={NAMES[person]}
      className="relative shrink-0 rounded-full transition disabled:cursor-default"
    >
      {photo ? (
        <img
          src={photo}
          alt=""
          width={256}
          height={256}
          decoding="async"
          className={`block h-28 w-28 rounded-full bg-dusty-teal/25 object-cover transition sm:h-36 sm:w-36 ${disc}`}
        />
      ) : (
        <span
          className={`flex h-28 w-28 items-center justify-center rounded-full bg-dusty-teal/25 font-serif text-6xl text-pine transition sm:h-36 sm:w-36 ${disc}`}
        >
          {person === "karishma" ? "K" : "B"}
        </span>
      )}
      {revealed && (isAnswer || isGuess) && (
        <span
          aria-hidden="true"
          className={`absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full text-sm text-cream ${
            isAnswer ? "bg-midnight-teal" : "bg-dusty-rose"
          }`}
        >
          {isAnswer ? "✓" : "✗"}
        </span>
      )}
    </button>
  );
}
